// Considered: FilterProvenanceGroupMolecule — rejected because it groups *filter* rows by
// provenance for the filter builder and is typed to filter entries, with no count-in-caption or
// per-group empty copy. This is a domain-neutral split of rows by where they come from —
// declared by this type, contributed by a composed aspect, inherited from an ancestor — used by
// Own properties (EAINT-895 Phase 3b), whose contract groups rows that way (`t-propgroup`,
// `type-composer.jsx:1419-1431`): PEER groups in the caller's order, each a heading (label ·
// count · note) over a tight list, and a group with nothing in it is not rendered; the aspect
// group is a peer, not a child of "Inherited". Relationships do NOT use it:
// their contract is one flat list with each inherited row marked in place (EAINT-897).
// See spec Design System Reuse Audit.
import React from 'react';
import './DeclaredInheritedGroupMolecule.scss';

export interface RowGroup {
  /** Stable key for the group, e.g. `own` / `aspect` / `inherited`. */
  id: string;
  /** The bold group name, e.g. "Declared by Vendor". */
  label: string;
  /** The row count shown beside the label. */
  count: number;
  /** The explanatory note after the count, e.g. "editable here". */
  note?: string;
  /** The group's rows. */
  children?: React.ReactNode;
  /**
   * Copy stated in place of the rows when there are none. A group without it is
   * simply not rendered when empty (`type-composer.jsx:1425`); a group WITH it is
   * a starting point — it stays, says so, and keeps whatever `footer` follows.
   */
  empty?: string;
  /** The `data-datum` the empty copy carries. */
  emptyDatum?: string;
  /** Rendered as the group's last child, after its rows or its empty copy — Own properties' add row. */
  footer?: React.ReactNode;
  /**
   * Parity self-description for the heading's four leaves — the contract's
   * `props > each(...) > group` shape (`props.heading` / `props.glabel` /
   * `props.rows-length` / `props.gnote`). Every group of one block carries the
   * same ids: they are one `each`.
   */
  datums?: { heading?: string; label?: string; count?: string; note?: string };
  'data-testid'?: string;
}

export interface DeclaredInheritedGroupMoleculeProps {
  /** The groups, in the order the contract lists them. */
  groups: RowGroup[];
  'data-testid'?: string;
}

const DeclaredInheritedGroupMolecule: React.FC<DeclaredInheritedGroupMoleculeProps> = ({ groups, 'data-testid': dataTestId }) => (
  <div className="declared-inherited-group" data-testid={dataTestId}>
    {groups
      .filter((group) => group.count > 0 || group.empty)
      .map((group) => (
        <div
          key={group.id}
          className="declared-inherited-group__section"
          data-container="each(groups) > group"
          data-group={group.id}
          data-testid={group['data-testid'] ?? `declared-inherited-group-${group.id}`}
        >
          <div
            className="declared-inherited-group__header"
            data-datum={group.datums?.heading}
            data-kind={group.datums?.heading ? 'heading' : undefined}
          >
            <strong className="declared-inherited-group__label" data-datum={group.datums?.label} data-kind={group.datums?.label ? 'strong' : undefined}>
              {group.label}
            </strong>
            <span className="declared-inherited-group__count" data-datum={group.datums?.count} data-kind={group.datums?.count ? 'count' : undefined}>
              {group.count}
            </span>
            {group.note && (
              <span className="declared-inherited-group__note" data-datum={group.datums?.note} data-kind={group.datums?.note ? 'hint' : undefined}>
                {group.note}
              </span>
            )}
          </div>
          {group.count === 0 ? (
            <p className="declared-inherited-group__empty-note" data-datum={group.emptyDatum} data-kind="note">
              {group.empty}
            </p>
          ) : (
            <div className="declared-inherited-group__rows" data-container="list">
              {group.children}
            </div>
          )}
          {group.footer}
        </div>
      ))}
  </div>
);

DeclaredInheritedGroupMolecule.displayName = 'DeclaredInheritedGroupMolecule';

export default DeclaredInheritedGroupMolecule;
